import {
    Extension,
    findChildren,
    combineTransactionSteps,
    getChangedRanges,
    findChildrenInRange,
    findDuplicates,
} from "@tiptap/core";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { Slice, Fragment } from "@tiptap/pm/model";
import { v4 } from "uuid";

export interface UniqueIdOptions {
    attributeName: string;
    types: string[];
    createId: () => string;
}

const UniqueId = new Extension<UniqueIdOptions>({
    name: "unique-id",
    priority: 99999,
    addOptions: () => ({
        attributeName: "id",
        types: [],
        createId: () => v4(),
    }),
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    [this.options.attributeName]: {
                        default: null,
                        parseHTML: (t) =>
                            t.getAttribute(this.options.attributeName),
                        renderHTML: (t) =>
                            t[this.options.attributeName]
                                ? {
                                      [this.options.attributeName]:
                                          t[this.options.attributeName],
                                  }
                                : {},
                    },
                },
            },
        ];
    },
    onCreate() {
        const { tr: transaction, doc } = this.editor.state;
        const { types, attributeName, createId } = this.options;

        const children = findChildren(
            doc,
            (node) =>
                (types as string[]).includes(node.type.name) &&
                null == node.attrs[attributeName],
        );

        children.forEach(({ node, pos }) => {
            transaction.setNodeMarkup(pos, void 0, {
                ...node.attrs,
                [attributeName]: createId(),
            });
        });

        this.editor.view.dispatch(transaction);
    },
    addProseMirrorPlugins() {
        let dragElement: HTMLElement | null = null;
        let isActive: boolean = false;
        return [
            new Plugin({
                key: new PluginKey("unique-id"),
                appendTransaction: (
                    transactions,
                    { doc: oldDoc },
                    { doc: newDoc, tr: newTransaction },
                ) => {
                    if (
                        !transactions.some(({ docChanged: t }) => t) ||
                        oldDoc.eq(newDoc)
                    )
                        return;

                    const { types, attributeName, createId } = this.options;
                    const combinedTransactionSteps = combineTransactionSteps(
                        oldDoc,
                        transactions as Transaction[],
                    );

                    if (
                        (getChangedRanges(combinedTransactionSteps).forEach(
                            ({ newRange }) => {
                                const childNodes = findChildrenInRange(
                                    newDoc,
                                    newRange,
                                    (node) =>
                                        (types as string[]).includes(
                                            node.type.name,
                                        ),
                                );

                                const filteredChildNodes = childNodes
                                    .map(
                                        ({ node }) => node.attrs[attributeName],
                                    )
                                    .filter((node) => null !== node);

                                childNodes.forEach(({ node, pos }) => {
                                    let nodeAtPos;
                                    const isNodeAtPos =
                                        null ===
                                            (nodeAtPos =
                                                newTransaction.doc.nodeAt(
                                                    pos,
                                                )) || void 0 === nodeAtPos
                                            ? void 0
                                            : nodeAtPos.attrs[attributeName];

                                    if (null === isNodeAtPos) {
                                        newTransaction.setNodeMarkup(
                                            pos,
                                            void 0,
                                            {
                                                ...node.attrs,
                                                [attributeName]: createId(),
                                            },
                                        );
                                        return;
                                    }

                                    if (
                                        !combinedTransactionSteps.mapping
                                            .invert()
                                            .mapResult(pos)
                                    )
                                        return;
                                    if (
                                        !findDuplicates(
                                            filteredChildNodes,
                                        ).includes(isNodeAtPos)
                                    )
                                        return;

                                    newTransaction.setNodeMarkup(pos, void 0, {
                                        ...node.attrs,
                                        [attributeName]: createId(),
                                    });
                                });
                            },
                        ),
                        newTransaction.steps.length)
                    )
                        return newTransaction;
                },
                view(editorView) {
                    const onDragStart = (event: DragEvent) => {
                        let parentElement;
                        dragElement = (
                            null ===
                                (parentElement =
                                    editorView.dom.parentElement) ||
                            void 0 === parentElement
                                ? void 0
                                : parentElement.contains(
                                      event.target as HTMLElement,
                                  )
                        )
                            ? editorView.dom.parentElement
                            : null;
                    };

                    return (
                        window.addEventListener("dragstart", onDragStart),
                        {
                            destroy() {
                                window.removeEventListener(
                                    "dragstart",
                                    onDragStart,
                                );
                            },
                        }
                    );
                },
                props: {
                    handleDOMEvents: {
                        drop: (editorView, event: DragEvent) => {
                            let dataTransfer;
                            return (
                                (dragElement !== editorView.dom.parentElement ||
                                    (null ===
                                        (dataTransfer = event.dataTransfer) ||
                                    void 0 === dataTransfer
                                        ? void 0
                                        : dataTransfer.effectAllowed) ===
                                        "copy") &&
                                    ((dragElement = null), (isActive = true)),
                                !1
                            );
                        },
                        paste: () => ((isActive = !0), !1),
                    },
                    transformPasted: (slice: Slice) => {
                        if (!isActive) return slice;

                        const { types, attributeName } = this.options;
                        const transformFragement = (fragment: Fragment) => {
                            const nodes: ReturnType<Fragment["maybeChild"]>[] =
                                [];

                            return (
                                fragment.forEach((node) => {
                                    if (node.isText) {
                                        nodes.push(node);
                                        return;
                                    }
                                    if (
                                        !(types as string[]).includes(
                                            node.type.name,
                                        )
                                    ) {
                                        nodes.push(
                                            node.copy(
                                                transformFragement(
                                                    node.content,
                                                ),
                                            ),
                                        );
                                        return;
                                    }

                                    nodes.push(
                                        node.type.create(
                                            {
                                                ...node.attrs,
                                                [attributeName]: null,
                                            },
                                            transformFragement(node.content),
                                            node.marks,
                                        ),
                                    );
                                }),
                                Fragment.from(
                                    nodes.filter((node) => node != null),
                                )
                            );
                        };
                        return (
                            (isActive = false),
                            new Slice(
                                transformFragement(slice.content),
                                slice.openStart,
                                slice.openEnd,
                            )
                        );
                    },
                },
            }),
        ];
    },
});

export default UniqueId;
